import moment from 'moment';
import { Thread, spawn } from 'threads';
import type { ModuleThread } from 'threads';
import { addMultipleBeatData, syncBeatInfo } from '@/shared/lib';
import emitter from '@/shared/lib/eventEmitter';
import { EMITTER_CONSTANTS } from '@/shared/config';
import type { BeatsWorkerApi } from './beatsWorkerApi';

// isWorking is read below but never part of the exposed API (see terminateThread) -
// pre-existing; ModuleThread only carries the exposed methods.
type BeatsThread = ModuleThread<BeatsWorkerApi> & { isWorking?: boolean };

/**
 * Instantiates the module worker that downloads and decodes beat and ECG files. The worker script URL is resolved relative to this module so the bundler emits it as its own chunk.
 *
 * @returns A new beats worker instance.
 */
const createBeatsWorker = () =>
  new Worker(new URL('./beats.worker.ts', import.meta.url), { type: 'module' });

// Function to terminate thread
/**
 * Shuts down a spawned beats worker thread once its download is finished or abandoned. Termination is awaited and both success and failure are logged rather than propagated.
 *
 * @param thread - The worker thread to terminate.
 * @returns A promise resolving once termination has been attempted.
 */
export const terminateThread = async (thread: BeatsThread) => {
  try {
    await Thread.terminate(thread);
    console.log('[WORKER] Thread terminated successfully.');
  } catch (error) {
    console.error('[WORKER] Failed to terminate thread:', error);
  }
};

// Function to handle downloading beat list
/**
 * Spawns the beats worker for a study's beat files and wires the message handler that persists each decoded batch. A busy worker is terminated and respawned, then the sampling frequency and query signature are pushed as worker config. Each beatData message collects any HTTP error codes and appends its beats to a serialised write chain so writes commit in order; on the final batch it waits for every write, then rebuilds beatInfo from what IndexedDB actually holds via syncBeatInfo and emits the completion event. A 403 anywhere emits the re-migrate event and stops the thread.
 *
 * @param ecgDataMap - Beat files to download in this run (a re-download subset for updateBeat), carrying the link list, sampling frequency and query signature.
 * @param updateBeat - True for a partial re-download; keeps the stored startedAt instead of stamping a new one.
 * @param fullMap - The full study data map used to recompute beatInfo; defaults to ecgDataMap for a full download.
 * @returns A promise resolving to the spawned worker thread, or to undefined when spawning failed.
 */
export const handleDownloadBeatList = async (
  ecgDataMap: any,
  updateBeat = false,
  fullMap: any = ecgDataMap,
) => {
  let threadBeat: BeatsThread;
  const errorCodes = new Set<any>();
  const startedAt = moment().valueOf();
  // Serialises the per-batch writes so the final syncBeatInfo reads a fully-committed store.
  let writeChain: Promise<unknown> = Promise.resolve();
  try {
    // Initialize worker and spawn a thread
    let worker = createBeatsWorker();
    threadBeat = await spawn<BeatsWorkerApi>(worker);

    // Check if the worker is already working, create a new one if necessary
    if (threadBeat.isWorking) {
      console.log('[WORKER BEAT] Worker is busy, creating a new worker.');
      await terminateThread(threadBeat);
      worker = createBeatsWorker();
      threadBeat = await spawn<BeatsWorkerApi>(worker);
    }

    await threadBeat.createConfig({
      samplingFrequency: ecgDataMap?.samplingFrequency,
      querySignature: ecgDataMap?.querySignature,
    });

    // Define how the worker should handle incoming messages
    worker.onmessage = async (message) => {
      const { type, data } = message.data;
      if (type !== 'beatData') return;
      const { result, metadata, lastItemProcessed } = data;

      if (metadata?.failedLinks?.length > 0) {
        metadata.failedLinks.forEach((link: any) => {
          if (link.errorCode !== undefined) errorCodes.add(link.errorCode);
        });
      }

      if (errorCodes.has(403)) {
        emitter.emit(EMITTER_CONSTANTS.RE_MIGRATE_FILE_DOWNLOAD_403);
        // Dừng download ngay khi gặp lỗi 403
        await terminateThread(threadBeat);
        return;
      }

      writeChain = writeChain.then(() => addMultipleBeatData(result));

      if (lastItemProcessed) {
        await writeChain;
        // Rebuild beatInfo from the ground truth (what IndexedDB actually holds).
        await syncBeatInfo(fullMap, {
          errorCodes: [...errorCodes],
          startedAt: updateBeat ? undefined : startedAt,
        });
        emitter.emit(EMITTER_CONSTANTS.VERIFY_ALL_BEAT_FILES_DOWNLOADED);
      }
    };
    // Return the thread so it can be used outside this function
    return threadBeat;
  } catch (error) {
    console.error('[WORKER] Failed to download beat list:', error);
    if (errorCodes.has(403)) {
      emitter.emit(EMITTER_CONSTANTS.RE_MIGRATE_FILE_DOWNLOAD_403);
    }
  }
};

// Function to group data by epoch start of the day
/**
 * Buckets beat or ECG link records by the calendar period their timestamp falls in, so downloads can be issued day by day. A reduce reads the chosen date field off each item, truncates it with moment's startOf and uses the resulting epoch as the bucket key.
 *
 * @param data - Link records to bucket.
 * @param typeGroup - Moment unit the timestamp is truncated to, such as day.
 * @param dateKey - Name of the field holding each record's timestamp.
 * @returns The records grouped under their period-start epoch keys.
 */
export const groupDataByEpochStartOfDay = (
  data: any[],
  typeGroup: moment.unitOfTime.StartOf,
  dateKey: string,
) =>
  data.reduce((acc: Record<string, any[]>, item: any) => {
    const dateEpoch = moment(item[dateKey]).startOf(typeGroup).valueOf();
    if (!acc[dateEpoch]) {
      acc[dateEpoch] = [];
    }
    acc[dateEpoch].push(item);
    return acc;
  }, {});

// Function to download data by date epoch
/**
 * Drives the worker through a beat-only download one calendar day at a time. Each day's grouped links are serialised and awaited in turn with a flag marking the final day so the worker can signal completion, and a thrown day aborts the loop with a logged error.
 *
 * @param thread - Spawned beats worker thread performing the download.
 * @param groupedData - Beat links keyed by start-of-day epoch.
 * @param querySignature - Signature identifying the query the download belongs to.
 * @returns A promise resolving to false when a day throws, and to undefined once every day has been requested.
 */
export const downloadOnlyBeatByDateEpoch = async (
  thread: BeatsThread,
  groupedData: Record<string, any>,
  querySignature: string,
) => {
  try {
    let lastItemProcessed = false;
    for (const dateEpoch of Object.keys(groupedData)) {
      const data = groupedData[dateEpoch];
      if (dateEpoch === Object.keys(groupedData)[Object.keys(groupedData).length - 1]) {
        lastItemProcessed = true;
      }
      await thread.downloadBeatListByDay(
        dateEpoch,
        JSON.stringify(data),
        lastItemProcessed,
        querySignature,
      );
    }
  } catch (error) {
    console.error('[WORKER] Failed to download data by date epoch:', error);
    return false;
  }
};
